/**
 * Landing Page Manager - Admin Control System
 * Allows full control over Early Access landing page content, layout, and motion
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Save, RotateCcw, Type, Layout, Zap, 
  ChevronUp, ChevronDown, Eye, EyeOff,
  ExternalLink, Loader2, AlertCircle
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Slider } from './ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from './ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from './ui/alert-dialog';

const API = process.env.REACT_APP_BACKEND_URL;

// Easing presets with CSS values
const EASING_PRESETS = {
  smooth: { label: 'Smooth', css: 'cubic-bezier(0.16, 1, 0.3, 1)' },
  calm: { label: 'Calm', css: 'cubic-bezier(0.4, 0, 0.2, 1)' },
  snappy: { label: 'Snappy', css: 'cubic-bezier(0.22, 1, 0.36, 1)' },
};

// Spacing presets
const SPACING_PRESETS = {
  tight: { label: 'Tight', value: 'tight' },
  normal: { label: 'Normal', value: 'normal' },
  roomy: { label: 'Roomy', value: 'roomy' },
};

const LandingPageManager = () => {
  const [settings, setSettings] = useState(null);
  const [originalSettings, setOriginalSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [isDefault, setIsDefault] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [activeTab, setActiveTab] = useState('content');

  // Fetch settings on mount
  useEffect(() => {
    fetchSettings();
  }, []);

  // Track changes
  useEffect(() => {
    if (settings && originalSettings) {
      const changed = JSON.stringify(settings) !== JSON.stringify(originalSettings);
      setHasChanges(changed);
    }
  }, [settings, originalSettings]);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/admin/landing-page/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSettings(response.data.settings);
      setOriginalSettings(response.data.settings);
      setIsDefault(response.data.is_default);
      setLastUpdated(response.data.last_updated);
    } catch (error) {
      console.error('Error fetching landing page settings:', error);
      toast.error('Failed to load landing page settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/admin/landing-page/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setOriginalSettings(settings);
      setHasChanges(false);
      setIsDefault(false);
      setLastUpdated(new Date().toISOString());
      toast.success('Landing page settings saved successfully');
    } catch (error) {
      console.error('Error saving landing page settings:', error);
      toast.error('Failed to save landing page settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/api/admin/landing-page/reset`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSettings(response.data.settings);
      setOriginalSettings(response.data.settings);
      setHasChanges(false);
      setIsDefault(true);
      setLastUpdated(null);
      toast.success('Landing page settings reset to defaults');
    } catch (error) {
      console.error('Error resetting landing page settings:', error);
      toast.error('Failed to reset landing page settings');
    } finally {
      setSaving(false);
    }
  };

  const updateContent = useCallback((field, value) => {
    setSettings(prev => ({
      ...prev,
      content: { ...prev.content, [field]: value }
    }));
  }, []);

  const updateLayout = useCallback((field, value) => {
    setSettings(prev => ({
      ...prev,
      layout: { ...prev.layout, [field]: value }
    }));
  }, []);

  const updateMotion = useCallback((field, value) => {
    setSettings(prev => ({
      ...prev,
      motion: { ...prev.motion, [field]: value }
    }));
  }, []);

  const toggleSection = useCallback((sectionId) => {
    setSettings(prev => ({
      ...prev,
      layout: {
        ...prev.layout,
        sections: prev.layout.sections.map(s =>
          s.id === sectionId ? { ...s, enabled: !s.enabled } : s
        )
      }
    }));
  }, []);

  const moveSection = useCallback((sectionId, direction) => {
    setSettings(prev => {
      const sections = [...prev.layout.sections].sort((a, b) => a.order - b.order);
      const index = sections.findIndex(s => s.id === sectionId);
      
      if (direction === 'up' && index > 0) {
        const temp = sections[index].order;
        sections[index].order = sections[index - 1].order;
        sections[index - 1].order = temp;
      } else if (direction === 'down' && index < sections.length - 1) {
        const temp = sections[index].order;
        sections[index].order = sections[index + 1].order;
        sections[index + 1].order = temp;
      }
      
      return {
        ...prev,
        layout: { ...prev.layout, sections }
      };
    });
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="text-center py-12 text-zinc-400">
        <AlertCircle className="w-12 h-12 mx-auto mb-4 text-red-500" />
        <p>Failed to load landing page settings</p>
        <Button onClick={fetchSettings} className="mt-4">Retry</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white">Landing Page Settings</h2>
          <p className="text-sm text-zinc-400 mt-1">
            Control the Early Access landing page content, layout, and animations
          </p>
          {lastUpdated && (
            <p className="text-xs text-zinc-500 mt-1">
              Last updated: {new Date(lastUpdated).toLocaleString()}
            </p>
          )}
        </div>
        
        <div className="flex items-center gap-3">
          {/* Preview Link */}
          <a
            href="/early-access"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors"
          >
            <ExternalLink className="w-4 h-4" />
            Preview
          </a>
          
          {/* Reset Button */}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                disabled={saving || isDefault}
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Reset
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-zinc-900 border-zinc-700">
              <AlertDialogHeader>
                <AlertDialogTitle className="text-white">Reset to Defaults?</AlertDialogTitle>
                <AlertDialogDescription className="text-zinc-400">
                  This will reset all landing page settings to their default values.
                  This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700">
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleReset}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  Reset
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          
          {/* Save Button */}
          <Button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className="bg-orange-500 hover:bg-orange-600 text-white"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save Changes
          </Button>
        </div>
      </div>

      {/* Unsaved Changes Warning */}
      {hasChanges && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg px-4 py-3 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0" />
          <span className="text-sm text-yellow-200">You have unsaved changes</span>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-zinc-800/50 border border-zinc-700 p-1">
          <TabsTrigger
            value="content"
            className="data-[state=active]:bg-orange-500 data-[state=active]:text-white"
          >
            <Type className="w-4 h-4 mr-2" />
            Content
          </TabsTrigger>
          <TabsTrigger
            value="layout"
            className="data-[state=active]:bg-orange-500 data-[state=active]:text-white"
          >
            <Layout className="w-4 h-4 mr-2" />
            Layout
          </TabsTrigger>
          <TabsTrigger
            value="motion"
            className="data-[state=active]:bg-orange-500 data-[state=active]:text-white"
          >
            <Zap className="w-4 h-4 mr-2" />
            Motion
          </TabsTrigger>
        </TabsList>

        {/* TAB 1: Content */}
        <TabsContent value="content" className="space-y-6">
          <div className="bg-zinc-800/30 border border-zinc-700 rounded-lg p-6 space-y-6">
            <h3 className="text-lg font-semibold text-white border-b border-zinc-700 pb-3">
              Hero Content
            </h3>
            
            {/* Early Access Label */}
            <div className="space-y-2">
              <Label className="text-zinc-300">Early Access Label</Label>
              <Input
                value={settings.content.early_access_label}
                onChange={(e) => updateContent('early_access_label', e.target.value)}
                placeholder="Early Access"
                className="bg-zinc-800 border-zinc-600 text-white"
              />
              <p className="text-xs text-zinc-500">Small text above the logo</p>
            </div>
            
            {/* Tagline */}
            <div className="space-y-2">
              <Label className="text-zinc-300">Tagline</Label>
              <Textarea
                value={settings.content.tagline}
                onChange={(e) => updateContent('tagline', e.target.value)}
                placeholder="Enter tagline..."
                className="bg-zinc-800 border-zinc-600 text-white min-h-[100px]"
              />
              <p className="text-xs text-zinc-500">Main message below the logo (1-2 sentences recommended)</p>
            </div>
            
            <div className="border-t border-zinc-700 pt-6">
              <h4 className="text-md font-medium text-white mb-4">Call-to-Action Buttons</h4>
              
              {/* Primary CTA */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Primary CTA Label</Label>
                  <Input
                    value={settings.content.primary_cta_label}
                    onChange={(e) => updateContent('primary_cta_label', e.target.value)}
                    placeholder="Start Creating"
                    className="bg-zinc-800 border-zinc-600 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Primary CTA Destination</Label>
                  <Input
                    value={settings.content.primary_cta_destination}
                    onChange={(e) => updateContent('primary_cta_destination', e.target.value)}
                    placeholder="/auth?mode=signup"
                    className="bg-zinc-800 border-zinc-600 text-white"
                  />
                </div>
              </div>
              
              {/* Secondary CTA */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Secondary CTA Label</Label>
                  <Input
                    value={settings.content.secondary_cta_label}
                    onChange={(e) => updateContent('secondary_cta_label', e.target.value)}
                    placeholder="Explore Platform"
                    className="bg-zinc-800 border-zinc-600 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Secondary CTA Destination</Label>
                  <Input
                    value={settings.content.secondary_cta_destination}
                    onChange={(e) => updateContent('secondary_cta_destination', e.target.value)}
                    placeholder="/"
                    className="bg-zinc-800 border-zinc-600 text-white"
                  />
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* TAB 2: Layout */}
        <TabsContent value="layout" className="space-y-6">
          <div className="bg-zinc-800/30 border border-zinc-700 rounded-lg p-6 space-y-6">
            <h3 className="text-lg font-semibold text-white border-b border-zinc-700 pb-3">
              Section Visibility & Order
            </h3>
            
            {/* Sections List */}
            <div className="space-y-2">
              {settings.layout.sections
                .sort((a, b) => a.order - b.order)
                .map((section, index) => (
                  <div
                    key={section.id}
                    className={`flex items-center justify-between p-4 rounded-lg border ${
                      section.enabled
                        ? 'bg-zinc-800/50 border-zinc-600'
                        : 'bg-zinc-900/50 border-zinc-800 opacity-60'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="flex flex-col gap-1">
                        <button
                          onClick={() => moveSection(section.id, 'up')}
                          disabled={index === 0}
                          className="p-1 hover:bg-zinc-700 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <ChevronUp className="w-4 h-4 text-zinc-400" />
                        </button>
                        <button
                          onClick={() => moveSection(section.id, 'down')}
                          disabled={index === settings.layout.sections.length - 1}
                          className="p-1 hover:bg-zinc-700 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <ChevronDown className="w-4 h-4 text-zinc-400" />
                        </button>
                      </div>
                      
                      <div>
                        <span className="text-white font-medium">{section.label}</span>
                        <span className="text-zinc-500 text-sm ml-2">#{section.order}</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-zinc-500">
                        {section.enabled ? 'Visible' : 'Hidden'}
                      </span>
                      <button
                        onClick={() => toggleSection(section.id)}
                        className={`p-2 rounded-lg transition-colors ${
                          section.enabled
                            ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                            : 'bg-zinc-700 text-zinc-400 hover:bg-zinc-600'
                        }`}
                      >
                        {section.enabled ? (
                          <Eye className="w-4 h-4" />
                        ) : (
                          <EyeOff className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>
                ))}
            </div>
            
            {/* Spacing Preset */}
            <div className="border-t border-zinc-700 pt-6">
              <div className="space-y-2">
                <Label className="text-zinc-300">Spacing Preset</Label>
                <Select
                  value={settings.layout.spacing_preset}
                  onValueChange={(value) => updateLayout('spacing_preset', value)}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-600 text-white w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-600">
                    {Object.entries(SPACING_PRESETS).map(([key, preset]) => (
                      <SelectItem key={key} value={key} className="text-white hover:bg-zinc-700">
                        {preset.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-zinc-500">Controls spacing between sections</p>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* TAB 3: Motion */}
        <TabsContent value="motion" className="space-y-6">
          <div className="bg-zinc-800/30 border border-zinc-700 rounded-lg p-6 space-y-6">
            <h3 className="text-lg font-semibold text-white border-b border-zinc-700 pb-3">
              Animation Settings
            </h3>
            
            {/* Hero Duration */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-zinc-300">Hero Animation Duration</Label>
                <span className="text-sm text-zinc-400 font-mono">
                  {settings.motion.hero_duration}ms
                </span>
              </div>
              <Slider
                value={[settings.motion.hero_duration]}
                onValueChange={([value]) => updateMotion('hero_duration', value)}
                min={200}
                max={1500}
                step={50}
                className="w-full"
              />
              <p className="text-xs text-zinc-500">Duration for logo and hero element animations (200-1500ms)</p>
            </div>
            
            {/* Stagger Delay */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-zinc-300">Stagger Delay</Label>
                <span className="text-sm text-zinc-400 font-mono">
                  {settings.motion.stagger_delay}ms
                </span>
              </div>
              <Slider
                value={[settings.motion.stagger_delay]}
                onValueChange={([value]) => updateMotion('stagger_delay', value)}
                min={50}
                max={400}
                step={25}
                className="w-full"
              />
              <p className="text-xs text-zinc-500">Delay between sequential hero elements (50-400ms)</p>
            </div>
            
            {/* Easing Preset */}
            <div className="space-y-2">
              <Label className="text-zinc-300">Easing Preset</Label>
              <Select
                value={settings.motion.easing_preset}
                onValueChange={(value) => updateMotion('easing_preset', value)}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-600 text-white w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-600">
                  {Object.entries(EASING_PRESETS).map(([key, preset]) => (
                    <SelectItem key={key} value={key} className="text-white hover:bg-zinc-700">
                      {preset.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-zinc-500">Animation easing curve feel</p>
            </div>
            
            {/* Reduced Motion */}
            <div className="flex items-center justify-between pt-4 border-t border-zinc-700">
              <div>
                <Label className="text-zinc-300">Respect Reduced Motion</Label>
                <p className="text-xs text-zinc-500 mt-1">
                  Disable animations for users with prefers-reduced-motion enabled
                </p>
              </div>
              <Switch
                checked={settings.motion.respect_reduced_motion}
                onCheckedChange={(checked) => updateMotion('respect_reduced_motion', checked)}
              />
            </div>
          </div>
          
          {/* Animation Preview Info */}
          <div className="bg-zinc-800/30 border border-zinc-700 rounded-lg p-4">
            <h4 className="text-sm font-medium text-white mb-2">Animation Sequence</h4>
            <div className="flex items-center gap-2 text-xs text-zinc-400">
              <span className="px-2 py-1 bg-zinc-700 rounded">Early Access</span>
              <span>→</span>
              <span className="px-2 py-1 bg-zinc-700 rounded">+{settings.motion.stagger_delay}ms</span>
              <span className="px-2 py-1 bg-zinc-700 rounded">Logo</span>
              <span>→</span>
              <span className="px-2 py-1 bg-zinc-700 rounded">+{settings.motion.stagger_delay}ms</span>
              <span className="px-2 py-1 bg-zinc-700 rounded">Tagline</span>
              <span>→</span>
              <span className="px-2 py-1 bg-zinc-700 rounded">+{settings.motion.stagger_delay}ms</span>
              <span className="px-2 py-1 bg-zinc-700 rounded">Buttons</span>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default LandingPageManager;
